import { useState } from 'react';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import type { RootState } from '@/state/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { useOnboardingForm } from '@/components/smartai/ComponentOnboarding/useOnboardingForm';
import type { FieldConfig } from '@/components/smartai/ComponentOnboarding/useOnboardingForm';
import {
  useStartManualSession,
  useEndManualSession,
  useMarkManualSessionResult,
  useFetchTestCasesFromAdo,
  useSubmitSessionTestCases,
  type StartSessionResponse,
  type EndSessionResponse,
  type AdoTestCase,
  type AdoTestCaseGroup,
  type SubmitSessionTestCasesResponse,
  type TaggedTestCaseResult,
} from '@/components/smartai/api/manualTesting';
import { cn } from '@/lib/utils';
import {
  mtView,
  mtPageTitle,
  mtPageSub,
  mtCardWrap,
  mtCardTitle,
  mtError,
  mtBtnRow,
  mtBtnRowTop,
  mtContextCard,
  mtContextTitle,
  mtContextGrid,
  mtContentContainer,
  mtContextLabel,
  mtModeGrid,
  mtModeCard,
  mtModeCardActive,
  mtModeTitle,
  mtModeDesc,
  mtModeBadge,
  mtWarn,
  mtWarnText,
  mtDetailGrid,
  mtDetailLabel,
  mtActiveWrap,
  mtActiveTitle,
  mtActiveSub,
  mtRunningIcon,
  mtPulseDot,
  mtPulseDotRed,
  mtSummaryGrid,
  mtSummaryGrid3col,
  mtSummaryCell,
  mtSummaryN,
  mtSummaryL,
  mtTagAskText,
  adoTree,
  adoSuite,
  adoSuiteHeader,
  adoSuiteCount,
  adoTc,
  adoTcBtn,
  adoTcSelected,
  adoTcPass,
  adoTcFail,
  tcCheckItem,
  tcCheckbox,
  tcCheckboxChecked,
  tcIdBadge,
  tcTitle,
  tcInlineActions,
  tcInlineBtn,
  tcInlineBtnPass,
  tcInlineBtnFail,
  tcInlineBtnPassActive,
  tcInlineBtnFailActive,
  mtPriorityBadge,
  mtResultBadge,
  mtResultBadgePass,
  mtResultBadgeFail,
  mtProgressStrip,
  mtProgItem,
  mtProgDot,
  mtProgDotPass,
  mtProgDotFail,
  mtProgDotActive,
  mtProgDotPending,
  mtProgId,
  mtProgRemaining,
  mtTcCard,
  mtTcCardHeader,
  mtTcTitle,
  mtTcCaptureStatus,
  mtBtnPass,
  mtBtnFail,
  mtResultBox,
  mtResultBoxPass,
  mtResultBoxFail,
  mtResultsList,
  mtResultRow,
  mtResultRowTitle,
} from './manual-testing-layout';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import { getApiErrorMessage } from '@/utils';
import { inferScmProviderFromRepoUrl } from '@/types/scm-provider';
import GitHubManualTestingView from './GitHubManualTestingView';

// ── Init form schema & fields (stable — defined outside component) ──────────

const initSchema = z.object({
  testerName: z.string().min(1, 'Please enter your name or tester ID.'),
  targetAppUrl: z.string().min(84, 'PAT must be at least 84 characters.'),
  referenceId: z.string(),
  testPlan: z.string().min(1, 'Please enter a test plan ID.'),
  suiteId: z.string().min(1, 'Please enter a suite ID.'),
});

type InitFormValues = z.infer<typeof initSchema>;

const INIT_FIELDS: FieldConfig<InitFormValues>[] = [
  {
    name: 'testerName',
    label: 'Your Name / Tester ID',
    type: 'text',
    placeholder: 'Enter your Name',
  },
  {
    name: 'targetAppUrl',
    label: 'Personal Access Token',
    type: 'password',
  },
  {
    name: 'referenceId',
    label: 'Reference ID (optional)',
    type: 'text',
    placeholder: 'e.g. Sprint-24, UAT-May2026, Release-247',
  },
  {
    name: 'testPlan',
    label: 'Test Plan ID',
    type: 'text',
    placeholder: 'ADO test plan ID (numeric)',
  },
  {
    name: 'suiteId',
    label: 'Suite ID',
    type: 'text',
    placeholder: 'ADO suite ID (numeric)',
  },
];

// ── Screen state machine ────────────────────────────────────────────────────

type Screen =
  | 'init'
  | 'mode-select'
  | 'sess-confirm'
  | 'sess-active'
  | 'sess-tag-ask'
  | 'sess-tag'
  | 'tag-done'
  | 'tc-select'
  | 'tc-run'
  | 'tc-result'
  | 'tc-done';

// ── Types ───────────────────────────────────────────────────────────────────

interface TCResult {
  testCaseId: string;
  name: string;
  suiteId: number;
  suiteName: string;
  priority: string;
  result: 'pass' | 'fail';
}

// ── View ────────────────────────────────────────────────────────────────────

const ManualTestingView = () => {
  const { ready, isLoading, notFound } = useSelectedComponent();
  const userSelectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );

  const [screen, setScreen] = useState<Screen>('init');
  const [initData, setInitData] = useState<InitFormValues>({
    testerName: '',
    targetAppUrl: '',
    referenceId: '',
    testPlan: '',
    suiteId: '',
  });
  const [apiError, setApiError] = useState('');
  const [sessionData, setSessionData] = useState<StartSessionResponse | null>(
    null,
  );
  const [endData, setEndData] = useState<EndSessionResponse | null>(null);
  const [suiteGroups, setSuiteGroups] = useState<AdoTestCaseGroup[]>([]);
  const [checkedTCIds, setCheckedTCIds] = useState<Set<string>>(new Set());
  const [tcResultMap, setTCResultMap] = useState<
    Record<string, 'pass' | 'fail'>
  >({});
  const [currentTC, setCurrentTC] = useState<AdoTestCase | null>(null);
  const [currentTCSuite, setCurrentTCSuite] = useState<AdoTestCaseGroup | null>(
    null,
  );
  const [tcResults, setTCResults] = useState<TCResult[]>([]);
  const [selectedNextTC, setSelectedNextTC] = useState<AdoTestCase | null>(
    null,
  );
  const [lastTCResult, setLastTCResult] = useState<'pass' | 'fail' | null>(
    null,
  );
  const [startingTCId, setStartingTCId] = useState<string | null>(null);
  const [perTcRunId, setPerTcRunId] = useState<string | null>(null);
  const [tagSubmitResponse, setTagSubmitResponse] =
    useState<SubmitSessionTestCasesResponse | null>(null);
  const { mutate: startSession, isPending: isStarting } =
    useStartManualSession();
  const { mutate: endSession, isPending: isEnding } = useEndManualSession();
  const { mutate: markSessionResult, isPending: isMarkingTC } =
    useMarkManualSessionResult();
  const { mutate: fetchTCs, isPending: isFetchingTCs } =
    useFetchTestCasesFromAdo(initData.targetAppUrl);
  const { mutate: submitResults, isPending: isSaving } =
    useSubmitSessionTestCases();

  // ── Init form ──────────────────────────────────────────────────────────

  const { renderFields, onSubmit: handleInitFormSubmit } =
    useOnboardingForm<InitFormValues>({
      fields: INIT_FIELDS,
      defaultValues: {
        testerName: '',
        targetAppUrl: '',
        referenceId: '',
        testPlan: '',
        suiteId: '',
      },
      schema: initSchema,
      onSubmit: data => {
        setInitData(data);
        setScreen('mode-select');
      },
    });

  // ── Helpers ─────────────────────────────────────────────────────────────

  const allTestCases = suiteGroups.flatMap(s => s.testCases);
  const pendingTCs = allTestCases.filter(tc => !tcResultMap[tc.testCaseId]);

  const findTCSuite = (tcId: string): AdoTestCaseGroup | undefined =>
    suiteGroups.find(s => s.testCases.some(tc => tc.testCaseId === tcId));

  const fetchTestCases = (onSuccess: (groups: AdoTestCaseGroup[]) => void) => {
    setApiError('');
    fetchTCs(
      {
        organization: userSelectedComponent.orgName,
        project: userSelectedComponent.projectName || '',
        planId: parseInt(initData.testPlan, 10) || 0,
        suiteIds: [parseInt(initData.suiteId, 10) || 0],
      },
      {
        onSuccess: data => {
          setSuiteGroups(data.groups);
          onSuccess(data.groups);
        },
        onError: err => {
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to fetch test cases. Please try again.',
            ),
          );
        },
      },
    );
  };

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleStartSession = () => {
    setApiError('');
    startSession(
      {
        testerName: initData.testerName,
        targetAppUrl: userSelectedComponent.frontendUrl,
        scanId: userSelectedComponent.scanId || undefined,
        repoId: userSelectedComponent.dbRepoId || undefined,
        branchId: userSelectedComponent.branchId || undefined,
      },
      {
        onSuccess: data => {
          setSessionData(data);
          window.open(data?.launchUrl, '_blank');
          setScreen('sess-active');
        },
        onError: err => {
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to start session. Please try again.',
            ),
          );
        },
      },
    );
  };

  const handleEndSession = () => {
    if (!sessionData) return;
    setApiError('');
    endSession(sessionData.sessionId, {
      onSuccess: data => {
        setEndData(data);
        setScreen('sess-tag-ask');
      },
      onError: err => {
        setApiError(
          getApiErrorMessage(err, 'Failed to end session. Please try again.'),
        );
      },
    });
  };

  const handleFetchTCsForTagging = () => {
    fetchTestCases(() => setScreen('sess-tag'));
  };

  const handleFetchTCsForPerTC = () => {
    setPerTcRunId(crypto.randomUUID());
    fetchTestCases(() => setScreen('tc-select'));
  };

  const handleToggleCheck = (tcId: string) => {
    setCheckedTCIds(prev => {
      const next = new Set(prev);
      if (next.has(tcId)) next.delete(tcId);
      else next.add(tcId);
      return next;
    });
  };

  const handleSaveTagResults = () => {
    if (!sessionData) {
      handleReset();
      return;
    }
    setApiError('');
    const results = Array.from(checkedTCIds)
      .filter(id => tcResultMap[id])
      .map(id => {
        const tc = allTestCases.find(t => t.testCaseId === id)!;
        const suite = findTCSuite(id);
        return {
          testCaseId: id,
          name: tc.name,
          suiteId: suite?.suiteId ?? 0,
          suiteName: suite?.suiteName ?? '',
          priority: tc.priority,
          status: (tcResultMap[id] === 'pass' ? 'passed' : 'failed') as
            | 'passed'
            | 'failed'
            | 'skipped',
          note: null,
        };
      });
    submitResults(
      {
        sessionId: sessionData.sessionId,
        body: { taggingState: 'tagged', results },
      },
      {
        onSuccess: data => {
          setTagSubmitResponse(data);
          setScreen('tag-done');
        },
        onError: err => {
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to save results. Please try again.',
            ),
          );
        },
      },
    );
  };

  const handleStartTCSession = (tc: AdoTestCase) => {
    setStartingTCId(tc.testCaseId);
    setSelectedNextTC(null);
    setApiError('');
    const suite = findTCSuite(tc.testCaseId);
    const referenceId = initData.referenceId?.trim();
    startSession(
      {
        testerName: initData.testerName,
        targetAppUrl: userSelectedComponent.frontendUrl,
        scanId: userSelectedComponent.scanId || undefined,
        repoId: userSelectedComponent.dbRepoId || undefined,
        branchId: userSelectedComponent.branchId || undefined,
        ...(referenceId ? { referenceId } : {}),
        testCaseId: tc.testCaseId,
        testCaseName: tc.name,
        suiteId: suite?.suiteId,
        suiteName: suite?.suiteName,
        priority: tc.priority || undefined,
        runId: perTcRunId || undefined,
      },
      {
        onSuccess: data => {
          setCurrentTC(tc);
          setCurrentTCSuite(suite ?? null);
          setSessionData(data);
          setStartingTCId(null);
          window.open(data?.launchUrl, '_blank');
          setScreen('tc-run');
        },
        onError: err => {
          setStartingTCId(null);
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to start test case session. Please try again.',
            ),
          );
        },
      },
    );
  };

  const handleMarkTC = (result: 'pass' | 'fail') => {
    if (!sessionData || !currentTC) return;
    setApiError('');
    const sessionId = sessionData.sessionId;
    const status = result === 'pass' ? 'passed' : 'failed';
    markSessionResult(
      { sessionId, body: { status } },
      {
        onSuccess: () => {
          endSession(sessionId, {
            onSuccess: () => {
              setTCResults(prev => [
                ...prev,
                {
                  testCaseId: currentTC.testCaseId,
                  name: currentTC.name,
                  suiteId: currentTCSuite?.suiteId ?? 0,
                  suiteName: currentTCSuite?.suiteName ?? '',
                  priority: currentTC.priority,
                  result,
                },
              ]);
              setTCResultMap(prev => ({
                ...prev,
                [currentTC.testCaseId]: result,
              }));
              setLastTCResult(result);
              setScreen('tc-result');
            },
            onError: err => {
              setApiError(
                getApiErrorMessage(
                  err,
                  'Failed to end test case session. Please try again.',
                ),
              );
            },
          });
        },
        onError: err => {
          setApiError(
            getApiErrorMessage(
              err,
              'Failed to record test case result. Please try again.',
            ),
          );
        },
      },
    );
  };

  const handleReset = () => {
    setScreen('init');
    setInitData({
      testerName: '',
      targetAppUrl: '',
      referenceId: '',
      testPlan: '',
      suiteId: '',
    });
    setApiError('');
    setSessionData(null);
    setEndData(null);
    setSuiteGroups([]);
    setCheckedTCIds(new Set());
    setTCResultMap({});
    setCurrentTC(null);
    setCurrentTCSuite(null);
    setTCResults([]);
    setSelectedNextTC(null);
    setLastTCResult(null);
    setStartingTCId(null);
    setPerTcRunId(null);
    setTagSubmitResponse(null);
  };

  // ── Selection gate ─────────────────────────────────────────────────────

  if (isLoading || (!ready && !notFound))
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  if (notFound) return <ComponentNotFound />;

  const scmProvider =
    userSelectedComponent.provider ??
    inferScmProviderFromRepoUrl(userSelectedComponent.repoUrl ?? '');
  if (scmProvider === 'github') {
    return <GitHubManualTestingView />;
  }

  // ── Screen: Init ───────────────────────────────────────────────────────

  if (screen === 'init') {
    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Start Manual Testing Session</div>
        <div className={mtPageSub}>
          Enter your details to begin. These are used for this session only.
        </div>

        <div className={mtCardWrap}>
          <Card>
            <CardContent className="p-4">
              <div className={mtCardTitle}>Session Credentials</div>
              <form onSubmit={handleInitFormSubmit}>
                {renderFields()}
                <div className={mtBtnRow}>
                  <Button type="submit">Begin Session</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {userSelectedComponent.repoName && (
          <div className={mtContextCard}>
            <div className={mtContextTitle}>Session Context</div>
            <div className={mtContextGrid}>
              <div className={mtContentContainer}>
                <div className={mtContextLabel}>Repo</div>
                <strong>{userSelectedComponent.repoName}</strong>
              </div>
              <div className={mtContentContainer}>
                <div className={mtContextLabel}>Branch</div>
                <strong>{userSelectedComponent.branchName || '—'}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Screen: Mode Select ────────────────────────────────────────────────

  if (screen === 'mode-select') {
    return (
      <div className={mtView}>
        <div className={cn(mtBtnRow, mtBtnRowTop)}>
          <Button variant="secondary" onClick={() => setScreen('init')}>
            Back
          </Button>
        </div>

        <div className={mtPageTitle}>Start Manual Testing</div>
        <div className={mtPageSub}>
          Choose how you want to capture coverage.
        </div>

        {apiError && (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        )}

        <div className={mtModeGrid}>
          <div
            className={cn(mtModeCard, mtModeCardActive)}
            onClick={() => setScreen('sess-confirm')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setScreen('sess-confirm')}>
            <div className={mtModeTitle}>Record Session Coverage</div>
            <div className={mtModeDesc}>
              Capture coverage for your whole session in one go. Optionally tag
              test cases and mark pass/fail at the end.
            </div>
          </div>

          <div
            className={cn(mtModeCard, mtModeCardActive)}
            onClick={!isFetchingTCs ? handleFetchTCsForPerTC : undefined}
            role="button"
            tabIndex={0}
            onKeyDown={e =>
              e.key === 'Enter' && !isFetchingTCs && handleFetchTCsForPerTC()
            }>
            <div className={mtModeTitle}>Record per Test Case</div>
            <div className={mtModeDesc}>
              Run ADO test cases one by one. Coverage is mapped to each test
              case. Mark pass or fail after each and move to the next.
            </div>
            {isFetchingTCs && <div className={mtModeBadge}>Loading…</div>}
          </div>
        </div>
      </div>
    );
  }

  // ── Screen: Session Confirm ────────────────────────────────────────────

  if (screen === 'sess-confirm') {
    return (
      <div className={mtView}>
        <div className={cn(mtBtnRow, mtBtnRowTop)}>
          <Button variant="secondary" onClick={() => setScreen('mode-select')}>
            Back
          </Button>
        </div>

        <div className={mtPageTitle}>Session Coverage</div>

        <div className={mtWarn}>
          <div className={mtWarnText}>
            <strong>
              This captures whole-session coverage — not per test case.
            </strong>
            <br />
            Everything you execute while the session is active is captured as
            one combined record.
          </div>
        </div>

        <div className={mtCardWrap}>
          <Card>
            <CardContent className="p-4">
              <div className={mtCardTitle}>Session Details</div>
              <div className={mtDetailGrid}>
                <div>
                  <div className={mtDetailLabel}>Tester</div>
                  <strong>{initData.testerName}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Mode</div>
                  <strong>Session Coverage</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Repo</div>
                  <strong>{userSelectedComponent.repoName || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Branch</div>
                  <strong>{userSelectedComponent.branchName || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Test Plan ID</div>
                  <strong>{initData.testPlan || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Suite ID</div>
                  <strong>{initData.suiteId || '—'}</strong>
                </div>
                <div>
                  <div className={mtDetailLabel}>Reference</div>
                  <strong>{initData.referenceId || '—'}</strong>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {apiError && (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        )}

        <div className={mtBtnRow}>
          <Button disabled={isStarting} onClick={handleStartSession}>
            {isStarting ? <Spinner compact /> : null}
            Start Session
          </Button>
          <Button variant="secondary" onClick={() => setScreen('mode-select')}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // ── Screen: Session Active ─────────────────────────────────────────────

  if (screen === 'sess-active' && sessionData) {
    return (
      <div className={mtView}>
        <div className={mtActiveWrap}>
          <div className={mtRunningIcon}>
            <span className={cn(mtPulseDot, mtPulseDotRed)} />
          </div>
          <div className={mtActiveTitle}>Session is Running</div>
          <div className={mtActiveSub}>
            Use the application in the opened window. Every method triggered is
            being recorded.
          </div>

          {apiError && (
            <div className={mtError} role="alert">
              {apiError}
            </div>
          )}

          <div className={mtBtnRow}>
            <Button disabled={isEnding} onClick={handleEndSession}>
              {isEnding ? <Spinner compact /> : null}
              End Session
            </Button>
            <Button variant="secondary" onClick={handleReset}>
              Start New Session
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Screen: Tag Ask ────────────────────────────────────────────────────

  if (screen === 'sess-tag-ask' && endData) {
    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Session Ended</div>
        <div className={mtPageSub}>
          Coverage has been saved for this session.
        </div>

        <div className={cn(mtSummaryGrid, mtSummaryGrid3col)}>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#d85604]')}>
              {endData.coverageSummary?.functions?.covered ?? 0}
            </div>
            <div className={mtSummaryL}>Methods Hit</div>
          </div>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#2563eb]')}>
              {endData.filesCovered?.length ?? endData.totalFiles ?? 0}
            </div>
            <div className={mtSummaryL}>Files</div>
          </div>
          <div className={mtSummaryCell}>
            <div className={mtSummaryN}>{endData.duration}</div>
            <div className={mtSummaryL}>Duration</div>
          </div>
        </div>

        <div className={mtCardWrap}>
          <Card>
            <CardContent className="p-4">
              <div className={mtCardTitle}>Tag Test Cases</div>
              <p className={mtTagAskText}>
                Do you want to tag test cases to this session&apos;s coverage and
                mark pass / fail?
              </p>
            </CardContent>
          </Card>
        </div>

        {apiError && (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        )}

        <div className={mtBtnRow}>
          <Button disabled={isFetchingTCs} onClick={handleFetchTCsForTagging}>
            {isFetchingTCs ? <Spinner compact /> : null}
            Yes — Tag Test Cases
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            No — Done
          </Button>
        </div>
      </div>
    );
  }

  // ── Screen: Tag Test Cases ─────────────────────────────────────────────

  if (screen === 'sess-tag') {
    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Tag Test Cases</div>
        <div className={mtPageSub}>
          Select the test cases you executed and mark them pass or fail.
        </div>

        <div className={adoTree}>
          {suiteGroups.map(suite => (
            <div key={suite.suiteId} className={adoSuite}>
              <div className={adoSuiteHeader}>
                <span>{suite.suiteName}</span>
                <span className={adoSuiteCount}>{suite.testCases.length}</span>
              </div>
              {suite.testCases.map(tc => {
                const isChecked = checkedTCIds.has(tc.testCaseId);
                const result = tcResultMap[tc.testCaseId];
                return (
                  <div
                    key={tc.testCaseId}
                    className={cn(
                      adoTc,
                      result === 'pass' && adoTcPass,
                      result === 'fail' && adoTcFail,
                    )}>
                    <div className={tcCheckItem}>
                      <span
                        className={cn(
                          tcCheckbox,
                          isChecked && tcCheckboxChecked,
                        )}
                        role="checkbox"
                        aria-checked={isChecked}
                        tabIndex={0}
                        onClick={() => handleToggleCheck(tc.testCaseId)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggleCheck(tc.testCaseId);
                          }
                        }}>
                        {isChecked ? '✓' : null}
                      </span>
                      <span className={tcIdBadge}>{tc.testCaseId}</span>
                      <span className={tcTitle}>{tc.name}</span>
                      <span className={mtPriorityBadge}>{tc.priority}</span>
                    </div>
                    {isChecked && (
                      <div className={tcInlineActions}>
                        <Button
                          variant="ghost"
                          className={cn(
                            tcInlineBtn,
                            tcInlineBtnPass,
                            result === 'pass' && tcInlineBtnPassActive,
                          )}
                          onClick={() =>
                            setTCResultMap(prev => ({
                              ...prev,
                              [tc.testCaseId]: 'pass',
                            }))
                          }>
                          Pass
                        </Button>
                        <Button
                          variant="ghost"
                          className={cn(
                            tcInlineBtn,
                            tcInlineBtnFail,
                            result === 'fail' && tcInlineBtnFailActive,
                          )}
                          onClick={() =>
                            setTCResultMap(prev => ({
                              ...prev,
                              [tc.testCaseId]: 'fail',
                            }))
                          }>
                          Fail
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {apiError && (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        )}

        <div className={mtBtnRow}>
          <Button disabled={isSaving} onClick={handleSaveTagResults}>
            {isSaving ? <Spinner compact /> : null}
            Save &amp; Done
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            Skip — Done
          </Button>
        </div>
      </div>
    );
  }

  // ── Screen: TC Select ──────────────────────────────────────────────────

  if (screen === 'tc-select') {
    return (
      <div className={mtView}>
        <div className={cn(mtBtnRow, mtBtnRowTop)}>
          <Button variant="secondary" onClick={() => setScreen('mode-select')}>
            Cancel
          </Button>
        </div>

        <div className={mtPageTitle}>Select a Test Case</div>
        <div className={mtPageSub}>
          Click a test case to open a coverage session for it in a new window.
        </div>

        {apiError && (
          <div className={mtError} role="alert">
            {apiError}
          </div>
        )}

        <div className={adoTree}>
          {suiteGroups.map(suite => (
            <div key={suite.suiteId} className={adoSuite}>
              <div className={adoSuiteHeader}>
                <span>{suite.suiteName}</span>
                <span className={adoSuiteCount}>{suite.testCases.length}</span>
              </div>
              {suite.testCases.map(tc => {
                const result = tcResultMap[tc.testCaseId];
                const isStartingThis = startingTCId === tc.testCaseId;
                return (
                  <div
                    key={tc.testCaseId}
                    className={cn(
                      adoTc,
                      adoTcBtn,
                      result === 'pass' && adoTcPass,
                      result === 'fail' && adoTcFail,
                    )}
                    role="button"
                    tabIndex={0}
                    onClick={() => !isStarting && handleStartTCSession(tc)}
                    onKeyDown={e =>
                      e.key === 'Enter' &&
                      !isStarting &&
                      handleStartTCSession(tc)
                    }>
                    <div>
                      <span className={tcIdBadge}>{tc.testCaseId}</span>
                      <span className={mtPriorityBadge}>{tc.priority}</span>
                    </div>

                    <h3 className={tcTitle}>{tc.name}</h3>
                    {result && (
                      <span
                        className={cn(
                          mtResultBadge,
                          result === 'pass'
                            ? mtResultBadgePass
                            : mtResultBadgeFail,
                        )}>
                        {result === 'pass' ? 'Pass' : 'Fail'}
                      </span>
                    )}
                    {isStartingThis && (
                      <span className={mtModeBadge}>Starting…</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Screen: TC Run ─────────────────────────────────────────────────────

  if (screen === 'tc-run' && currentTC) {
    return (
      <div className={mtView}>
        <div className={mtProgressStrip}>
          {allTestCases.map(tc => {
            const res = tcResultMap[tc.testCaseId];
            const isCurrent = tc.testCaseId === currentTC.testCaseId;
            return (
              <div key={tc.testCaseId} className={mtProgItem}>
                <span
                  className={cn(
                    mtProgDot,
                    isCurrent
                      ? mtProgDotActive
                      : res === 'pass'
                        ? mtProgDotPass
                        : res === 'fail'
                          ? mtProgDotFail
                          : mtProgDotPending,
                  )}
                />
                <span className={mtProgId}>{tc.testCaseId}</span>
              </div>
            );
          })}
          <div className={mtProgRemaining}>{pendingTCs.length} remaining</div>
        </div>

        <div className={mtTcCard}>
          <div className={mtTcCardHeader}>
            <span className={tcIdBadge}>{currentTC.testCaseId}</span>
            <span className={mtPriorityBadge}>{currentTC.priority}</span>
            <div className={mtTcTitle}>{currentTC.name}</div>
          </div>

          <div className={mtTcCaptureStatus}>
            Coverage is being captured for this test case
          </div>

          {apiError && (
            <div className={mtError} role="alert">
              {apiError}
            </div>
          )}

          <div className={mtBtnRow}>
            <Button
              className={mtBtnPass}
              onClick={() => handleMarkTC('pass')}
              disabled={isEnding || isMarkingTC}>
              {isEnding || isMarkingTC ? '…' : 'Pass'}
            </Button>
            <Button
              variant="destructive"
              className={mtBtnFail}
              onClick={() => handleMarkTC('fail')}
              disabled={isEnding || isMarkingTC}>
              {isEnding || isMarkingTC ? '…' : 'Fail'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Screen: TC Result ──────────────────────────────────────────────────

  if (screen === 'tc-result' && currentTC && lastTCResult) {
    return (
      <div className={mtView}>
        <div
          className={cn(
            mtResultBox,
            lastTCResult === 'pass' ? mtResultBoxPass : mtResultBoxFail,
          )}>
          [{lastTCResult === 'pass' ? 'PASS' : 'FAIL'}]
        </div>

        {pendingTCs.length > 0 ? (
          <>
            <div className={cn(mtPageTitle, 'mt-6')}>Select Next Test Case</div>

            <div className={adoTree}>
              {suiteGroups.map(suite => {
                const remaining = suite.testCases.filter(
                  tc => !tcResultMap[tc.testCaseId],
                );
                if (remaining.length === 0) return null;
                return (
                  <div key={suite.suiteId} className={adoSuite}>
                    <div className={adoSuiteHeader}>
                      <span>{suite.suiteName}</span>
                      <span className={adoSuiteCount}>{remaining.length}</span>
                    </div>
                    {remaining.map(tc => (
                      <div
                        key={tc.testCaseId}
                        className={cn(
                          adoTc,
                          adoTcBtn,
                          selectedNextTC?.testCaseId === tc.testCaseId &&
                            adoTcSelected,
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedNextTC(tc)}
                        onKeyDown={e =>
                          e.key === 'Enter' && setSelectedNextTC(tc)
                        }>
                        <span className={tcIdBadge}>{tc.testCaseId}</span>
                        <span className={tcTitle}>{tc.name}</span>
                        <span className={mtPriorityBadge}>{tc.priority}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className={mtBtnRow}>
              {selectedNextTC && (
                <Button
                  disabled={isStarting}
                  onClick={() => handleStartTCSession(selectedNextTC)}>
                  {isStarting ? <Spinner compact /> : null}
                  Run Selected
                </Button>
              )}
              <Button variant="secondary" onClick={() => setScreen('tc-done')}>
                End Session
              </Button>
            </div>
          </>
        ) : (
          <div className={cn(mtBtnRow, 'mt-6')}>
            <Button onClick={() => setScreen('tc-done')}>View Summary</Button>
          </div>
        )}
      </div>
    );
  }

  // ── Screen: TC Done ────────────────────────────────────────────────────

  if (screen === 'tc-done') {
    const passed = tcResults.filter(r => r.result === 'pass').length;
    const failed = tcResults.filter(r => r.result === 'fail').length;

    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Session Complete</div>

        <div className={cn(mtSummaryGrid, mtSummaryGrid3col)}>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#d85604]')}>
              {tcResults.length}
            </div>
            <div className={mtSummaryL}>Test Cases Run</div>
          </div>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#16a34a]')}>{passed}</div>
            <div className={mtSummaryL}>Passed</div>
          </div>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#dc2626]')}>{failed}</div>
            <div className={mtSummaryL}>Failed</div>
          </div>
        </div>

        <div className={mtCardWrap}>
          <Card>
            <CardContent className="p-4">
              <div className={mtCardTitle}>Results</div>
              <div className={mtResultsList}>
                {tcResults.map(r => (
                  <div key={r.testCaseId} className={mtResultRow}>
                    <span className={tcIdBadge}>{r.testCaseId}</span>
                    <span className={mtResultRowTitle}>{r.name}</span>
                    <span
                      className={cn(
                        mtResultBadge,
                        r.result === 'pass'
                          ? mtResultBadgePass
                          : mtResultBadgeFail,
                      )}>
                      {r.result === 'pass' ? 'Pass' : 'Fail'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className={mtBtnRow}>
          <Button onClick={handleReset}>Start Another Session</Button>
        </div>
      </div>
    );
  }
  // ── Screen: Tag Done ───────────────────────────────────────────────────

  if (screen === 'tag-done' && tagSubmitResponse) {
    const groupedResults = tagSubmitResponse.results.reduce<
      Record<number, { suiteName: string; items: TaggedTestCaseResult[] }>
    >((acc, r) => {
      if (!acc[r.suiteId])
        acc[r.suiteId] = { suiteName: r.suiteName, items: [] };
      acc[r.suiteId].items.push(r);
      return acc;
    }, {});

    const passed = tagSubmitResponse.results.filter(
      r => r.status === 'passed',
    ).length;
    const failed = tagSubmitResponse.results.filter(
      r => r.status === 'failed',
    ).length;

    return (
      <div className={mtView}>
        <div className={mtPageTitle}>Tagging Complete</div>
        <div className={mtPageSub}>
          Test cases have been tagged to this session.
        </div>

        <div className={cn(mtSummaryGrid, mtSummaryGrid3col)}>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#16a34a]')}>{passed}</div>
            <div className={mtSummaryL}>Test Cases Passed</div>
          </div>
          <div className={mtSummaryCell}>
            <div className={cn(mtSummaryN, 'text-[#dc2626]')}>{failed}</div>
            <div className={mtSummaryL}>Test Cases Failed</div>
          </div>
        </div>

        <div className={adoTree}>
          {Object.entries(groupedResults).map(
            ([suiteId, { suiteName, items }]) => (
              <div key={suiteId} className={adoSuite}>
                <div className={adoSuiteHeader}>
                  <span>{suiteName}</span>
                  <span className={adoSuiteCount}>{items.length}</span>
                </div>
                {items.map(r => {
                  const resultKey =
                    r.status === 'passed'
                      ? 'pass'
                      : r.status === 'failed'
                        ? 'fail'
                        : 'skip';
                  return (
                    <div
                      key={r.testCaseId}
                      className={cn(
                        adoTc,
                        resultKey === 'pass' && adoTcPass,
                        resultKey === 'fail' && adoTcFail,
                      )}>
                      <span className={tcIdBadge}>{r.testCaseId}</span>
                      <span className={tcTitle}>{r.name}</span>
                      <span className={mtPriorityBadge}>{r.priority}</span>
                      <span
                        className={cn(
                          mtResultBadge,
                          resultKey === 'pass'
                            ? mtResultBadgePass
                            : resultKey === 'fail'
                              ? mtResultBadgeFail
                              : undefined,
                        )}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ),
          )}
        </div>

        <div className={mtBtnRow}>
          <Button onClick={handleReset}>Start New Session</Button>
        </div>
      </div>
    );
  }
  return null;
};

export default ManualTestingView;
